import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { useCourseCreation } from '../../../contexts/CourseCreationContext';
import {
  FileText,
  Save,
  Eye,
  AlertCircle,
  Upload,
  Link,
  Bold,
  Italic,
  List,
  Type
} from 'lucide-react';

const DocumentEditor = ({ content, onUpdate, onSave, onPreview }) => {
  const { validateContent } = useCourseCreation();
  const [errors, setErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleFieldChange = (field, value) => {
    const updatedContent = { ...content, [field]: value };
    onUpdate(updatedContent);
    
    const validation = validateContent(updatedContent, 'document');
    setErrors(validation.errors);
  };

  const handleSave = async () => {
    setIsValidating(true);
    const validation = validateContent(content, 'document');
    
    if (validation.valid) {
      await onSave(content);
      setErrors([]);
    } else {
      setErrors(validation.errors);
    }
    setIsValidating(false);
  };

  const addAttachment = () => {
    const newAttachment = {
      name: '',
      url: '',
      type: 'document'
    };
    
    const updatedAttachments = [...(content.attachments || []), newAttachment];
    handleFieldChange('attachments', updatedAttachments);
  };

  const updateAttachment = (index, updates) => {
    const updatedAttachments = (content.attachments || []).map((attachment, i) =>
      i === index ? { ...attachment, ...updates } : attachment
    );
    handleFieldChange('attachments', updatedAttachments);
  };

  const removeAttachment = (index) => {
    const updatedAttachments = (content.attachments || []).filter((_, i) => i !== index);
    handleFieldChange('attachments', updatedAttachments);
  };

  const formatText = (format) => {
    const textarea = document.getElementById('content-editor');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.content.substring(start, end);
    
    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'list':
        formattedText = `- ${selectedText}`;
        break;
      case 'heading':
        formattedText = `## ${selectedText}`;
        break;
      default:
        formattedText = selectedText;
    }
    
    const newContent = content.content.substring(0, start) + formattedText + content.content.substring(end);
    handleFieldChange('content', newContent);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-6 h-6 text-purple-500" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Éditeur de Document
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <Eye className="w-4 h-4" />
            {showPreview ? 'Éditer' : 'Aperçu'}
          </button>
          <button
            onClick={handleSave}
            disabled={isValidating || errors.length > 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            {isValidating ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Erreurs */}
      {errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
              Erreurs de validation
            </h3>
          </div>
          <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulaire */}
        <div className="space-y-6">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Titre du document *
            </label>
            <input
              type="text"
              value={content.title || ''}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder="Ex: Introduction au JavaScript"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description *
            </label>
            <textarea
              value={content.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Décrivez le contenu de ce document..."
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
            />
          </div>

          {/* Contenu */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Contenu du document *
              </label>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => formatText('bold')}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  title="Gras"
                >
                  <Bold className="w-4 h-4" />
                </button>
                <button
                  onClick={() => formatText('italic')}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  title="Italique"
                >
                  <Italic className="w-4 h-4" />
                </button>
                <button
                  onClick={() => formatText('heading')}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  title="Titre"
                >
                  <Type className="w-4 h-4" />
                </button>
                <button
                  onClick={() => formatText('list')}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  title="Liste"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
            <textarea
              id="content-editor"
              value={content.content || ''}
              onChange={(e) => handleFieldChange('content', e.target.value)}
              rows="12"
              placeholder="Rédigez le contenu de votre document en utilisant le Markdown..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none font-mono text-sm"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Utilisez le Markdown pour formater votre texte. Supporte **gras**, *italique*, ## titres, - listes, etc.
            </p>
          </div>

          {/* Fichiers joints */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Fichiers joints
              </label>
              <button
                onClick={addAttachment}
                className="flex items-center gap-1 px-3 py-1 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm"
              >
                <Upload className="w-4 h-4" />
                Ajouter
              </button>
            </div>
            
            <div className="space-y-2">
              {(content.attachments || []).map((attachment, index) => (
                <div key={index} className="flex items-center gap-2 p-2 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <input
                    type="text"
                    value={attachment.name}
                    onChange={(e) => updateAttachment(index, { name: e.target.value })}
                    placeholder="Nom du fichier"
                    className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                  />
                  <input
                    type="url"
                    value={attachment.url}
                    onChange={(e) => updateAttachment(index, { url: e.target.value })}
                    placeholder="URL du fichier"
                    className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                  />
                  <button
                    onClick={() => removeAttachment(index)}
                    className="p-1 text-red-500 hover:text-red-700 transition-colors"
                  >
                    <AlertCircle className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Aperçu */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {showPreview ? 'Aperçu du document' : 'Informations du document'}
          </h3>
          
          {showPreview ? (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {content.title || 'Titre du document'}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {content.description || 'Description du document'}
              </p>
              
              <div className="prose dark:prose-invert max-w-none">
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      content.content?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\*(.*?)\*/g, '<em>$1</em>')
                        .replace(/## (.*?)/g, '<h2>$1</h2>')
                        .replace(/- (.*?)/g, '<li>$1</li>')
                        .replace(/\n/g, '<br>') || 'Contenu du document...',
                      { ALLOWED_TAGS: ['strong','em','h2','li','br','p'], ALLOW_DATA_ATTR: false }
                    )
                  }}
                />
              </div>
              
              {(content.attachments || []).length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fichiers joints :
                  </h5>
                  <ul className="space-y-1">
                    {content.attachments.map((attachment, index) => (
                      <li key={index} className="text-sm">
                        <a 
                          href={attachment.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary/80 transition-colors"
                        >
                          {attachment.name || 'Fichier joint'}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                {content.title || 'Titre du document'}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {content.description || 'Description du document'}
              </p>
              
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  <span>Document</span>
                </div>
                <div className="flex items-center gap-1">
                  <Link className="w-3 h-3" />
                  <span>{(content.attachments || []).length} fichiers joints</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentEditor;
